// Mirror of CssfPdfExperiencesSection.jsx (forced French): bar title, total-years
// row, then per experience a 30/70 row (date left; details right with dash bullets).

import { AlignmentType } from "docx";
import { parseISO } from "date-fns";
import { calculateTotalExperienceDuration } from "../../../../utils/calculateDatesDifference";
import { formatDateCssf } from "../../../../utils/formatDate";
import { barSectionTitle, dashBullet, para, run, twoColumnRow } from "../../shared/wordHelpers";
import { CSSF_ACCENT, CSSF_TEXT, FONT_VERDANA } from "../../shared/wordTheme";
import { pxToPt, pxToTwips } from "../../shared/wordUnits";

const DOC_LANG = "fr"; // CSSF is always French

function rightLine( text, { px = 9, bold, after = 0 } = {} ) {
    return para( run( text, { pt: pxToPt( px ), bold, color: CSSF_TEXT, font: FONT_VERDANA } ), { spacing: { after } } );
}

export function buildCssfExperiences( { data, props, translations } ) {
    const { experiences, totalExperienceDuration } = data;
    const { isAnonymous, isExperienceHidden } = props;
    if ( isExperienceHidden ) {
        return [];
    }
    const t = translations?.pdf ?? {};
    const expSection = translations?.experiencesSection ?? {};

    const {
        years: displayYears,
        months: displayMonths,
    } = calculateTotalExperienceDuration( experiences, totalExperienceDuration );
    const durationStr = ( t.workExperienceTemplate ?? "" )
        .replace( "{years}", displayYears )
        .replace( "{months}", displayMonths )
        .replace( "(s)", "s" );

    const elements = [];
    elements.push( barSectionTitle( "EXPÉRIENCE\nPROFESSIONNELLE", { accent: CSSF_ACCENT, font: FONT_VERDANA } ) );

    elements.push( twoColumnRow(
        [ para( run( "Nombre d’année d’expérience", { pt: 9, color: CSSF_ACCENT, font: FONT_VERDANA, allCaps: true } ), { alignment: AlignmentType.RIGHT, spacing: { before: pxToTwips( 4 ), after: 0 } } ) ],
        [ para( run( durationStr, { pt: pxToPt( 11 ), color: CSSF_TEXT, font: FONT_VERDANA } ), { spacing: { before: pxToTwips( 8 ), after: 0 } } ) ],
        { rightMarginLeftPx: 12 }
    ) );

    experiences
        .slice()
        .filter( experience => experience )
        .sort( ( a, b ) => parseISO( b?.from ) - parseISO( a?.from ) )
        .forEach( experience => {
            const dateText = experience.from && ( experience.to || experience.isCurrent )
                ? `De ${ formatDateCssf( experience.from, DOC_LANG ) } à ${ experience.isCurrent ? t.isCurrentLabel : formatDateCssf( experience.to, DOC_LANG ) }`
                : `Depuis ${ formatDateCssf( experience.from, DOC_LANG ) }`;

            const left = [ para(
                run( dateText, { pt: 9, color: CSSF_ACCENT, font: FONT_VERDANA } ),
                { alignment: AlignmentType.RIGHT, spacing: { before: pxToTwips( 4 ), after: 0 } }
            ) ];

            const right = [];
            right.push( rightLine( experience.jobPosition, { px: 11, after: pxToTwips( 4 ) } ) );

            const sector = experience.activitySector?.translations?.[ DOC_LANG ];
            if ( sector ) {
                right.push( rightLine( `${ expSection.activitySectorLabel ?? "" } ${ sector }`, { px: 9, after: pxToTwips( 4 ) } ) );
            }
            const contractType = experience.contractType?.translations?.[ DOC_LANG ];
            if ( contractType ) {
                right.push( rightLine( `${ expSection.contractTypeLabel ?? "" } ${ contractType }`, { px: 9, after: pxToTwips( 4 ) } ) );
            }

            const validRoles = Array.isArray( experience.roles )
                ? experience.roles.filter( role => role?.text && role.text.trim() !== "" )
                : [];
            if ( validRoles.length > 0 ) {
                right.push( rightLine( t.rolesLabel, { px: 9 } ) );
                validRoles
                    .slice()
                    .sort( ( a, b ) => a.order - b.order )
                    .forEach( role => right.push( dashBullet( role.text, { pt: 9, color: CSSF_TEXT, font: FONT_VERDANA } ) ) );
            }

            if ( experience.context && experience.context.trim() !== "" ) {
                right.push( rightLine( experience.context, { px: 9, after: pxToTwips( 8 ) } ) );
            }
            if ( experience.achievement && experience.achievement.trim() !== "" ) {
                right.push( rightLine( `${ t.achievementsLabel ?? "" } ${ experience.achievement }`, { px: 9, after: pxToTwips( 8 ) } ) );
            }

            const hasActions = Array.isArray( experience.actions )
                && experience.actions.some( action => action?.text && action.text.trim() !== "" );
            if ( hasActions ) {
                right.push( rightLine( t.actionsLabel, { px: 9 } ) );
                experience.actions
                    .filter( action => action?.text && action.text.trim() )
                    .forEach( action => right.push( dashBullet( action.text, { pt: 9, color: CSSF_TEXT, font: FONT_VERDANA } ) ) );
            }

            if ( Array.isArray( experience.technicalEnvironment ) && experience.technicalEnvironment.length > 0 ) {
                const techText = experience.technicalEnvironment
                    .map( skill => skill?.translations?.[ DOC_LANG ] || skill )
                    .join( ", " );
                right.push( rightLine( `${ t.technicalEnvironmentLabel ?? "" } ${ techText }`, { px: 9, after: pxToTwips( 8 ) } ) );
            }

            if ( experience.isConsultingMission ) {
                right.push( para(
                    [
                        run( "En mission chez ", { pt: pxToPt( 9 ), color: CSSF_TEXT, font: FONT_VERDANA } ),
                        run( experience.client, { pt: pxToPt( 9 ), bold: true, color: CSSF_TEXT, font: FONT_VERDANA } ),
                    ],
                    { spacing: { after: pxToTwips( 8 ) } }
                ) );
            } else {
                right.push( rightLine( isAnonymous ? t.employerLabel : experience.employer, { px: 9, after: pxToTwips( 8 ) } ) );
            }

            elements.push( twoColumnRow( left, right, { rightMarginLeftPx: 12 } ) );
        } );

    return elements;
}
